import { useRef, useEffect, useState } from 'react';
import { Filter, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LiteModeBadge } from '../LiteModeBadge';
import { ContentTabs } from './ContentTabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConnectionModeSelector } from '../ConnectionModeSelector';
import { LiteModeSettings } from '../LiteModeSettings';
import { isFeatureEnabled } from '@/config/featureFlags';
import { useConnectionAware } from '../ConnectionAwareProvider';

interface StickyHeaderProps {
  filtersOpen: boolean;
  onToggleFilters: () => void;
  isLiteMode: boolean;
  selectedContentType?: 'posts' | 'casts' | 'my_teams';
  onContentTypeChange?: (type: 'posts' | 'casts' | 'my_teams') => void;
}

export const StickyHeader = ({
  filtersOpen,
  onToggleFilters,
  isLiteMode,
  selectedContentType = 'posts',
  onContentTypeChange
}: StickyHeaderProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const { isOffline, connectionType } = useConnectionAware();

  // Measure header height and write to CSS variable
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      const h = el.offsetHeight;
      document.documentElement.style.setProperty('--ys-header-h', `${h}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div 
      ref={ref}
      id="ys-sticky" 
      className="ys-safe-header sticky w-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700"
      style={{ top: "var(--ys-app-header-h, 0px)" }}
    >
      <div className="mx-auto max-w-2xl px-3 md:px-4 py-1 ys-touch-optimized">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-start gap-2">
            {isFeatureEnabled('CONNECTION_AWARE_EXPERIENCE') ? (
              <button 
                onClick={() => setShowSettings(true)}
                className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                aria-label="Open connection settings"
              >
                <LiteModeBadge />
              </button>
            ) : (
              <LiteModeBadge />
            )}
          </div>
          
          <Button 
            variant="ghost"
            size="sm" 
            className="h-9 px-3 rounded-xl border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 focus:ring-2 focus:ring-gray-300"
            onClick={onToggleFilters}
            aria-expanded={filtersOpen}
            aria-controls="feed-filters"
          >
            <Filter className="w-4 h-4 mr-1" />
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>

        {/* Content tabs - always shown when available, styled by mode */}
        {onContentTypeChange && (
          <div className="mt-1">
            <ContentTabs 
              mode={isLiteMode ? 'lite' : 'full'}
              selectedType={selectedContentType}
              onTypeChange={onContentTypeChange}
            />
          </div>
        )}
      </div>

      {/* Connection Settings Dialog */}
      {isFeatureEnabled('CONNECTION_AWARE_EXPERIENCE') && (
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="connection-desc">
            <DialogHeader>
              <div className="flex items-center justify-between gap-4">
                <DialogTitle className="text-xl">Connection-Aware Experience</DialogTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Current network speed:</span>
                  {isOffline ? <WifiOff className="h-4 w-4 text-red-500" /> : <Wifi className="h-4 w-4 text-green-500" />}
                  <Badge variant={isOffline ? "destructive" : "secondary"} className="text-xs">
                    {isOffline ? "Offline" : connectionType || "Online"}
                  </Badge>
                </div>
              </div>
            </DialogHeader>
            
            <p id="connection-desc" className="sr-only">
              Configure your connection mode and data usage preferences to optimize your YieldSocial experience based on your network conditions.
            </p>
            
            <div className="space-y-6">
              <ConnectionModeSelector />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
